import * as format from "~/business/college/format";
import { PoweredByNiche } from "~/components/atoms/PoweredByNiche";
import Image from "~/components/atoms/Image";
import { Tag } from "~/components/atoms/Tag";
import compact from "lodash/compact";
import { cn } from "~/lib/utils";
import { useScrollPosition } from "~/lib/scroll";
import { CollegeData } from "~/components/pages/CollegePage";
import IconCollegePlaceholder from "~/components/atoms/IconCollegePlaceholder";
import CollegeFavoriteStar from "~/components/organisms/CollegeFavoriteStar";

type Props = Pick<CollegeData, "college" | "conference" | "division" | "sport" | "favorited">;

const BigHeader = ({ college, conference, division, sport, favorited }: Props) => {
    const collegeLogoTitle = `${college.attrs.name} logo`;
    const collegeLogoClasses =
        "relative top-4 rounded-full border border-gray-20 bg-white sm:h-[144px] sm:w-[144px] flex items-center justify-center";

    return (
        <div className="border-b border-blue-20 bg-white">
            <div className="relative flex w-full justify-center bg-blue-90">
                <div className="w-[36px]" />
                <div className="relative flex max-w-6xl grow justify-center">
                    {college.attrs.logo_url ? (
                        <Image
                            alt={collegeLogoTitle}
                            src={college.attrs.logo_url}
                            className={cn(collegeLogoClasses, "size-24 sm:size-36")}
                        />
                    ) : (
                        <div className={cn(collegeLogoClasses, "p-6")}>
                            <IconCollegePlaceholder className="size-12" title={collegeLogoTitle} />
                        </div>
                    )}
                </div>
                <div className="relative right-3 top-3">
                    <CollegeFavoriteStar
                        collegeId={college.id as string}
                        collegeName={college.attrs.name}
                        favorited={favorited}
                    />
                </div>
            </div>
            <div className="flex flex-col items-center gap-4 px-4 text-center sm:gap-6">
                <h1 className="font-header-medium pt-6 uppercase text-blue-80 sm:pt-8">
                    {college.attrs.name}
                </h1>
                <Tag text={sport.name} data-testid="card-header-medium" />
                <p className="font-body text-gray-80">
                    {compact([
                        format.location(college),
                        division?.attrs.display_name,
                        conference?.attrs.name,
                    ]).join("\u30FB")}
                </p>
            </div>
            {/* You get your own row because Niche SVG demands it's own vertical padding 🙄 */}
            <div className="flex justify-center py-2 sm:py-4">
                <PoweredByNiche />
            </div>
        </div>
    );
};

const SmallHeader = ({ college, division, favorited }: Props) => {
    const collegeLogoTitle = `${college.attrs.name} logo`;
    const collegeLogoClasses = "mr-5 rounded-full border border-blue-80 sm:block";

    return (
        <div className="fixed top-0 z-50 flex w-full justify-center bg-blue-90 sm:hidden">
            <div className={"flex w-full max-w-7xl items-center justify-center px-5 py-3"}>
                {college.attrs.logo_url ? (
                    <Image
                        alt={collegeLogoTitle}
                        src={college.attrs.logo_url}
                        className={cn(collegeLogoClasses, "size-12 bg-white")}
                    />
                ) : (
                    <div className={cn(collegeLogoClasses, "bg-white p-[10px]")}>
                        <IconCollegePlaceholder
                            className={cn("size-6")}
                            color="blue"
                            title={collegeLogoTitle}
                        />
                    </div>
                )}
                <div className="flex grow flex-col">
                    <h6 className="font-subheader-1 uppercase text-white">{college.attrs.name}</h6>
                    <div className="font-metadata-medium whitespace-pre-wrap text-white">
                        {`${format.location(college)}${division ? "   |   " + format.shortName(division.attrs.display_name) : ""}`}
                    </div>
                </div>
                <CollegeFavoriteStar
                    collegeId={college.id as string}
                    collegeName={college.attrs.name}
                    favorited={favorited}
                />
            </div>
        </div>
    );
};

const CollegePageHeader = (props: Props) => {
    const scroll = useScrollPosition();
    return (
        <>
            <div
                className={cn(
                    "opacity-0 transition-opacity duration-500",
                    scroll > 350 ? "opacity-100" : "pointer-events-none",
                )}
            >
                <SmallHeader {...props} />
            </div>
            <BigHeader {...props} />
        </>
    );
};

export default CollegePageHeader;
